// ────────────────────────────────────────────────────────────────────────────
//  Punch.tsx — Stempeluhr (Live-Status, Pausen, History, Notizen)
//
//  Wenn keine PunchClock-Extension aktiv ist → Lock-Screen mit Shop-CTA.
//  Sonst: Live-Card mit Timer, Buttons (In/Out, Pause/Resume), Notiz-Feld,
//  Wochen-Stats und Verlaufs-Liste.
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useT, useLangCode } from '../i18n'
import { useExtensions } from '../lib/useExtensions'
import { useOrg } from '../lib/useOrg'
import {
  usePunchStatus, usePunchRecords,
  clockIn, clockOut, pauseStart, pauseEnd, deleteRecord,
  recordSeconds, fmtDuration,
  resetMyPunch, useMyArchives, getMyArchive, getTeamArchive,
  updateRecord, canEditRecords, getRecordAudit,
  type PunchRecord, type PunchArchive, type PunchArchiveDetail, type AuditEntry,
} from '../lib/usePunch'
import { useAuth } from '../lib/useAuth'

export function Punch() {
  const { has, hasBusinessTier, loading } = useExtensions()

  if (loading) return <Spinner />
  // Gate: PunchClock-Extension oder irgendein Business-Tier (das schließt
  // Stempeluhr automatisch ein).
  if (!has('PunchClock') && !hasBusinessTier()) return <Locked />

  return <PunchPanel />
}

// ── Locked State ────────────────────────────────────────────────────────────
function Locked() {
  const t = useT()
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="glass-strong p-8 md:p-10">
        <div className="text-6xl mb-4">🔒</div>
        <h1 className="text-2xl font-bold mb-2">{t('punch.locked')}</h1>
        <p className="text-app-text-dim leading-relaxed mb-6">{t('punch.lockedSub')}</p>
        <Link to="/app/shop" className="btn-primary">🛒 {t('punch.lockedShop')}</Link>
      </div>
    </div>
  )
}

// ── Hauptpanel ──────────────────────────────────────────────────────────────
function PunchPanel() {
  const t = useT()
  const { status, refresh } = usePunchStatus({ pollMs: 30_000 })
  const [range, setRange] = useState<'week' | 'month' | 'all'>('week')
  const { items: records, refresh: refreshRecords } = usePunchRecords(range)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const refreshAll = () => { refresh(); refreshRecords() }

  const onClockIn = async () => {
    setBusy(true); setErr(null)
    try { await clockIn(); refreshAll() }
    catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }
  const onClockOut = async () => {
    setBusy(true); setErr(null)
    try { await clockOut(); refreshAll() }
    catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }
  const onPause = async () => {
    setBusy(true); setErr(null)
    try { await pauseStart(); refresh() }
    catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }
  const onResume = async () => {
    setBusy(true); setErr(null)
    try { await pauseEnd(); refresh() }
    catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-5 sm:py-7 md:py-10 space-y-4 sm:space-y-5">
      <header>
        <h1 className="text-3xl md:text-4xl font-bold">{t('punch.title')}</h1>
        <p className="text-sm text-app-text-dim mt-1.5">{t('punch.sub')}</p>
      </header>

      <LiveStatusCard status={status} busy={busy} onIn={onClockIn} onOut={onClockOut} onPause={onPause} onResume={onResume} err={err} />

      <StatsRow records={records} />

      <RecordsCard records={records} range={range} setRange={setRange} onChanged={refreshRecords} />

      <ArchivesSection />
      <ResetSection onResetDone={refreshAll} />
    </div>
  )
}

// ── Reset-Section ───────────────────────────────────────────────────────────
function ResetSection({ onResetDone }: { onResetDone: () => void }) {
  const t = useT()
  const { org } = useOrg()
  const [showConfirm, setShowConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ count: number } | null>(null)
  const [err, setErr]   = useState<string | null>(null)

  const inOrg = !!org

  const submit = async () => {
    setBusy(true); setErr(null)
    try {
      const r = await resetMyPunch()
      setDone({ count: r.archivedCount })
      setShowConfirm(false)
      onResetDone()
      setTimeout(() => setDone(null), 4000)
    } catch (e: any) {
      setErr(e?.message ?? 'failed')
    } finally { setBusy(false) }
  }

  return (
    <section
      className="rounded-3xl p-5 md:p-7"
      style={{ background: 'rgba(255,179,0,0.04)', border: '1px solid rgba(255,179,0,0.25)' }}
    >
      <header className="mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <span className="text-lg">♻️</span>{t('punch.resetTitle')}
        </h3>
        <p className="text-xs text-app-text-mute mt-0.5 leading-relaxed">{t('punch.resetSub')}</p>
      </header>

      {inOrg ? (
        <div className="text-sm text-app-text-dim p-3 rounded-xl"
             style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
          🔒 {t('punch.resetBlockedOrg')}
        </div>
      ) : !showConfirm ? (
        <button onClick={() => setShowConfirm(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition"
          style={{ background: 'rgba(255,179,0,0.15)', border: '1px solid rgba(255,179,0,0.30)', color: '#ffb300' }}>
          ♻️ {t('punch.resetBtn')}
        </button>
      ) : (
        <div className="rounded-xl p-4" style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
          <div className="font-semibold text-sm mb-1">{t('punch.resetConfirmTitle')}</div>
          <p className="text-xs text-app-text-dim leading-relaxed mb-3">{t('punch.resetConfirmText')}</p>
          {err && <div className="text-sm text-coral mb-2">{err}</div>}
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: '#ff5b5c' }}>
              {busy ? '…' : t('punch.resetConfirmDo')}
            </button>
            <button onClick={() => { setShowConfirm(false); setErr(null) }} className="btn-ghost text-sm py-2 px-4">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {done && (
        <div className="mt-3 text-sm text-teal">
          {t('punch.resetDoneTitle')} · {t('punch.resetDoneN', { n: done.count })}
        </div>
      )}
    </section>
  )
}

// ── Archives-Section ────────────────────────────────────────────────────────
function ArchivesSection() {
  const t = useT()
  const lang = useLangCode()
  const { items, loading } = useMyArchives()
  const [openId, setOpenId] = useState<number | null>(null)

  if (loading) return null
  if (items.length === 0) return null

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <span className="text-lg">🗂️</span>{t('punch.archivesTitle')}
        </h3>
      </header>
      <ul className="space-y-2">
        {items.map(a => <ArchiveRow key={a.id} a={a} onOpen={() => setOpenId(a.id)} t={t} lang={lang} />)}
      </ul>
      {openId !== null && (
        <ArchiveDetailModal id={openId} mode="user" onClose={() => setOpenId(null)} />
      )}
    </section>
  )
}

function ArchiveRow({ a, onOpen, t, lang }: { a: PunchArchive; onOpen: () => void; t: any; lang: 'de' | 'en' }) {
  const { h, m } = fmtDuration(a.totalSeconds)
  const reasonLabel = t(`punch.archiveReason.${a.reason}`)
  const date = new Date(a.createdAt).toLocaleDateString(lang, { day: '2-digit', month: 'long', year: 'numeric' })
  return (
    <li>
      <button onClick={onOpen} className="w-full text-left rounded-xl glass-flat hover:bg-app-elev-2 transition p-4 flex flex-wrap items-center gap-3">
        <div className="grid place-items-center w-10 h-10 rounded-xl bg-accent/15 border border-accent/25 text-lg shrink-0">
          🗂
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{a.orgName ?? t('punch.archiveReason.user_reset')}</div>
          <div className="text-xs text-app-text-mute mt-0.5">
            {reasonLabel} · {t('punch.archiveMeta', { date })}
          </div>
        </div>
        <div className="text-sm text-right shrink-0">
          <div className="font-mono font-bold tabular-nums">{t('punch.durationFmt', { h, m })}</div>
          <div className="text-[10px] text-app-text-mute">{t('punch.archiveEntries', { n: a.totalEntries })}</div>
        </div>
      </button>
    </li>
  )
}

// ── Archive-Detail-Modal (wiederverwendbar User + Owner-Mode) ──────────────
export function ArchiveDetailModal({
  id, mode, onClose,
}: { id: number; mode: 'user' | 'team'; onClose: () => void }) {
  const t = useT()
  const lang = useLangCode()
  const [data, setData] = useState<PunchArchiveDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const loader = mode === 'user' ? getMyArchive : getTeamArchive
    loader(id)
      .then(d => { setData(d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [id, mode])

  return (
    <div className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-0 sm:p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-3xl glass-strong p-5 md:p-7 sm:rounded-3xl rounded-none max-h-screen overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="mb-4 flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-sm text-app-text-mute hover:text-app-text">{t('punch.archiveBack')}</button>
        </header>

        {loading || !data ? (
          <div className="text-center py-12 text-app-text-mute">…</div>
        ) : (
          <>
            <h2 className="text-2xl font-bold">{data.orgName ?? t('punch.archiveReason.user_reset')}</h2>
            <div className="text-sm text-app-text-dim mt-1">
              {t(`punch.archiveReason.${data.reason}`)}
              {' · '}{t('punch.archiveMeta', { date: new Date(data.createdAt).toLocaleDateString(lang, { day: '2-digit', month: 'long', year: 'numeric' }) })}
            </div>
            {mode === 'team' && data.userDisplayName && (
              <div className="mt-1 text-sm font-medium">{data.userDisplayName}{data.userEmail && <span className="text-app-text-mute"> · {data.userEmail}</span>}</div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
              <ArchiveStat label={t('punch.archiveTotal')}   value={t('punch.durationFmt', fmtDuration(data.totalSeconds))} />
              <ArchiveStat label={t('punch.archiveEntries', { n: data.totalEntries })} value={String(data.totalEntries)} />
              <ArchiveStat label={t('punch.archivePause')}   value={t('punch.durationFmt', fmtDuration(data.totalPauseSec))} />
            </div>

            <div className="mt-3 text-[11px] text-app-text-mute italic">{t('punch.archiveReadOnly')}</div>

            <ul className="mt-5 space-y-1.5">
              {data.entries.map(e => (
                <li key={e.id} className="rounded-lg glass-flat p-3 flex flex-wrap items-center gap-3">
                  <div className="text-xs text-app-text-mute font-medium w-24 shrink-0">
                    {new Date(e.clockIn).toLocaleDateString(lang, { day: '2-digit', month: '2-digit' })}
                  </div>
                  <div className="font-mono text-xs">
                    {new Date(e.clockIn).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}
                    {e.clockOut && <> – {new Date(e.clockOut).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}</>}
                  </div>
                  <div className="flex-1 min-w-0">
                    {e.note && <div className="text-xs text-app-text-dim truncate">{e.note}</div>}
                  </div>
                  {e.clockOut && (
                    <div className="font-mono text-sm font-semibold tabular-nums shrink-0">
                      {(() => { const { h, m } = fmtDuration(Math.max(0, Math.floor((new Date(e.clockOut).getTime() - new Date(e.clockIn).getTime())/1000) - e.pauseSeconds)); return `${h}h ${m}min` })()}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

function ArchiveStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl glass-flat p-3 text-center">
      <div className="text-xl font-bold tabular-nums">{value}</div>
      <div className="text-[10px] text-app-text-mute uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  )
}

// ── Live-Status-Card ────────────────────────────────────────────────────────
function LiveStatusCard({
  status, busy, onIn, onOut, onPause, onResume, err,
}: any) {
  const t = useT()
  const lang = useLangCode()
  const isIn  = status?.isClockedIn
  const isPaused = status?.isPaused

  // Live-Timer (sekündlich neu rendern wenn eingestempelt)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!isIn) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [isIn])

  const liveSec = (() => {
    if (!status?.currentRecord) return 0
    const start = new Date(status.currentRecord.clockIn).getTime()
    let total = Math.floor((now - start) / 1000) - (status.currentRecord.pauseSeconds ?? 0)
    if (isPaused && status.currentPause) {
      const pStart = new Date(status.currentPause.pausedAt).getTime()
      total -= Math.floor((now - pStart) / 1000)
    }
    return Math.max(0, total)
  })()
  const fmt = (s: number) => {
    const h = Math.floor(s / 3600).toString().padStart(2, '0')
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0')
    const sec = (s % 60).toString().padStart(2, '0')
    return `${h}:${m}:${sec}`
  }

  return (
    <section className="glass-strong p-6 md:p-8 relative overflow-hidden">
      <div className="absolute -top-20 -right-12 w-72 h-72 rounded-full opacity-50"
           style={{ background: isIn
             ? (isPaused
               ? 'radial-gradient(circle, rgba(255,179,0,0.20), transparent 70%)'
               : 'radial-gradient(circle, rgba(51,214,112,0.20), transparent 70%)')
             : 'radial-gradient(circle, rgba(138,68,238,0.18), transparent 70%)' }} />
      <div className="relative">
        <div className="flex flex-wrap items-start gap-4">
          <div className="grid place-items-center w-14 h-14 rounded-2xl shrink-0"
               style={{
                 background: isIn ? (isPaused ? 'rgba(255,179,0,0.15)' : 'rgba(51,214,112,0.15)') : 'rgba(255,255,255,0.05)',
                 border: `1px solid ${isIn ? (isPaused ? 'rgba(255,179,0,0.30)' : 'rgba(51,214,112,0.30)') : 'var(--border)'}`,
               }}>
            <span className="text-3xl">{isPaused ? '⏸' : isIn ? '▶' : '🕐'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold tracking-wider uppercase"
                 style={{ color: isIn ? (isPaused ? '#ffb300' : '#33d670') : 'var(--text-mute)' }}>
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${isIn ? (isPaused ? 'bg-orange-400' : 'bg-teal animate-pulse') : 'bg-app-text-mute'}`} />
                {isIn ? (isPaused ? t('punch.statusPaused') : t('punch.statusIn')) : t('punch.statusOut')}
              </span>
            </div>
            <div className="font-mono text-4xl md:text-5xl font-bold tabular-nums tracking-tight mt-1">
              {fmt(liveSec)}
            </div>
            {isIn && status?.currentRecord && (
              <div className="text-xs text-app-text-mute mt-1">
                {t('punch.sinceLabel', { time: new Date(status.currentRecord.clockIn).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }) })}
              </div>
            )}
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}

        <div className="mt-6 flex flex-wrap gap-2">
          {!isIn && (
            <button onClick={onIn} disabled={busy} className="btn-primary text-base py-3 px-6 disabled:opacity-50">
              {t('punch.clockIn')}
            </button>
          )}
          {isIn && !isPaused && (
            <>
              <button onClick={onOut} disabled={busy} className="btn-primary text-base py-3 px-6 disabled:opacity-50">
                {t('punch.clockOut')}
              </button>
              <button onClick={onPause} disabled={busy} className="btn-ghost text-base py-3 px-6 disabled:opacity-50">
                {t('punch.pauseStart')}
              </button>
            </>
          )}
          {isIn && isPaused && (
            <button onClick={onResume} disabled={busy} className="btn-primary text-base py-3 px-6 disabled:opacity-50">
              {t('punch.pauseEnd')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

// ── Stats Row (Heute / Diese Woche / Pausen) ───────────────────────────────
function StatsRow({ records }: { records: PunchRecord[] }) {
  const t = useT()
  const stats = useMemo(() => {
    const now = new Date()
    const todayStart = new Date(now); todayStart.setHours(0,0,0,0)
    const dow = (now.getDay() + 6) % 7
    const weekStart = new Date(todayStart); weekStart.setDate(weekStart.getDate() - dow)

    let todaySec = 0, todayPause = 0, weekSec = 0, dayCount = 0
    const dayDates = new Set<string>()
    for (const r of records) {
      const d = new Date(r.clockIn); d.setHours(0,0,0,0)
      if (d >= weekStart) {
        const sec = recordSeconds(r)
        weekSec += sec
        if (sec > 0) dayDates.add(d.toISOString().slice(0,10))
      }
      if (d.getTime() === todayStart.getTime()) {
        todaySec += recordSeconds(r)
        todayPause += r.pauseSeconds ?? 0
      }
    }
    dayCount = dayDates.size
    const avgPerDay = dayCount > 0 ? Math.round(weekSec / dayCount) : 0
    return { todaySec, todayPause, weekSec, avgPerDay }
  }, [records])

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      <StatTile icon="📅" label={t('punch.todayWorked')} value={fmtCompact(stats.todaySec, t)} tone="ok" />
      <StatTile icon="⏸" label={t('punch.todayPaused')} value={fmtCompact(stats.todayPause, t)} />
      <StatTile icon="📊" label={t('punch.weekTotal')} value={fmtCompact(stats.weekSec, t)} />
      <StatTile icon="∑" label={t('punch.weekAvg')} value={fmtCompact(stats.avgPerDay, t)} />
    </div>
  )
}

function StatTile({ icon, label, value, tone }: { icon: string; label: string; value: string; tone?: 'ok' }) {
  return (
    <div className="glass p-4 md:p-5">
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
        {tone === 'ok' && <span className="w-2 h-2 rounded-full bg-teal animate-pulse" />}
      </div>
      <div className="mt-2 text-2xl md:text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-[11px] text-app-text-mute font-medium tracking-wide uppercase mt-0.5">{label}</div>
    </div>
  )
}

// ── Records-Card (Verlauf) ─────────────────────────────────────────────────
function RecordsCard({ records, range, setRange, onChanged }: {
  records: PunchRecord[]; range: 'week'|'month'|'all'; setRange: (r: any) => void; onChanged: () => void
}) {
  const t = useT()
  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2">
          <span className="text-lg">📋</span>{t('punch.historyTitle')}
        </h3>
        <div className="grid grid-cols-3 gap-1 p-1 rounded-xl"
             style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
          {([
            { v: 'week' as const,  l: t('punch.rangeWeek') },
            { v: 'month' as const, l: t('punch.rangeMonth') },
            { v: 'all' as const,   l: t('punch.rangeAll') },
          ]).map(o => (
            <button key={o.v} onClick={() => setRange(o.v)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                range === o.v ? 'bg-accent text-white' : 'text-app-text-dim hover:text-app-text'
              }`}>
              {o.l}
            </button>
          ))}
        </div>
      </header>

      {records.length === 0 ? (
        <div className="text-sm text-app-text-mute text-center py-8">{t('punch.historyEmpty')}</div>
      ) : (
        <ul className="space-y-2">
          {records.map(r => <RecordRow key={r.id} r={r} t={t} onChanged={onChanged} />)}
        </ul>
      )}
    </section>
  )
}

function RecordRow({ r, t, onChanged }: { r: PunchRecord; t: any; onChanged: () => void }) {
  const lang = useLangCode()
  const { user } = useAuth()
  const sec = recordSeconds(r)
  const { h, m } = fmtDuration(sec)
  const date = formatRecordDate(r.clockIn, t, lang)
  const time = `${new Date(r.clockIn).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}` +
               (r.clockOut ? ` – ${new Date(r.clockOut).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}` : ' – …')
  const [editOpen, setEditOpen] = useState(false)

  // Permission-Check für Bearbeiten
  // (entspricht 1:1 dem Worker-Check, damit der UI-State stimmt)
  const myPerms = (user as any)?.myOrgPermissions as { canManageEmployeeProfiles?: boolean } | undefined
  const canEdit = canEditRecords(user as any, myPerms)

  const handleDelete = async () => {
    if (!canEdit) return
    if (!confirm(t('punch.deleteConfirm'))) return
    try { await deleteRecord(r.id); onChanged() } catch (e: any) { alert(e?.message) }
  }

  return (
    <li className="rounded-xl glass-flat p-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-[11px] font-bold tracking-wider uppercase text-app-text-mute w-20 shrink-0">{date}</div>
        <div className="font-mono text-sm">{time}</div>
        <div className="flex-1 min-w-0" />
        {r.isManual && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ background: 'rgba(255,179,0,0.15)', color: '#ffb300', border: '1px solid rgba(255,179,0,0.30)' }}>
            {t('punch.editManualBadge')}
          </span>
        )}
        <div className="font-bold tabular-nums">{r.clockOut ? t('punch.durationFmt', { h, m }) : '…'}</div>
        {(r.pauseSeconds ?? 0) > 0 && (
          <span className="text-[10px] text-app-text-mute">{t('punch.pauseFmt', { m: Math.round((r.pauseSeconds ?? 0) / 60) })}</span>
        )}
      </div>
      {r.note && (
        <div className="mt-2 ml-20 text-xs text-app-text-dim">{r.note}</div>
      )}
      {canEdit && (
        <div className="mt-2 ml-20 flex gap-3 text-[11px] text-app-text-mute">
          <button onClick={() => setEditOpen(true)} className="hover:text-app-text">📝 {t('punch.editBtn')}</button>
          <button onClick={handleDelete} className="hover:text-coral">🗑 {t('punch.deleteBtn')}</button>
        </div>
      )}
      {!canEdit && user?.orgId && (
        <div className="mt-2 ml-20 text-[10px] text-app-text-mute italic">🔒 {t('punch.editLockedNoPerm')}</div>
      )}
      {editOpen && <EditRecordModal record={r} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); onChanged() }} />}
    </li>
  )
}

// ── Edit-Record-Modal ──────────────────────────────────────────────────────
// Auch von Payroll.tsx genutzt um fremde Records zu bearbeiten.
export { EditRecordModal as EditRecordModalExternal }
function EditRecordModal({ record, onClose, onSaved }: { record: PunchRecord; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const [clockIn, setClockIn]   = useState(toLocalInputValue(record.clockIn))
  const [clockOut, setClockOut] = useState(record.clockOut ? toLocalInputValue(record.clockOut) : '')
  const [pauseMin, setPauseMin] = useState(String(Math.round((record.pauseSeconds ?? 0) / 60)))
  const [note, setNote]         = useState(record.note ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState<string | null>(null)
  const [ok, setOk]     = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true); setOk(false)
    const cInISO = fromLocalInputValue(clockIn)
    const cOutISO = clockOut ? fromLocalInputValue(clockOut) : null
    if (cOutISO && new Date(cOutISO) < new Date(cInISO)) {
      setErr(t('punch.editEndBeforeStart')); setBusy(false); return
    }
    try {
      await updateRecord(record.id, {
        clockIn:      cInISO,
        clockOut:     cOutISO,
        pauseSeconds: Math.max(0, parseInt(pauseMin || '0') * 60),
        note:         note.trim() || null,
      })
      setOk(true); setTimeout(onSaved, 500)
    } catch (e: any) {
      setErr(e?.message ?? 'failed')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-0 sm:p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-md glass-strong p-5 md:p-6 sm:rounded-3xl rounded-none"
           onClick={e => e.stopPropagation()}>
        <header className="mb-4">
          <h3 className="text-xl font-bold">{t('punch.editTitle')}</h3>
          <p className="text-xs text-app-text-mute mt-0.5">{t('punch.editSub')}</p>
        </header>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">{t('punch.editClockIn')}</span>
            <input type="datetime-local" required value={clockIn} onChange={e => setClockIn(e.target.value)} className="input-base" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">{t('punch.editClockOut')}</span>
            <input type="datetime-local" value={clockOut} onChange={e => setClockOut(e.target.value)} className="input-base" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">{t('punch.editPauseMin')}</span>
            <input type="number" min="0" value={pauseMin} onChange={e => setPauseMin(e.target.value)} className="input-base" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">{t('punch.editNote')}</span>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="input-base" placeholder={t('punch.notesPh')} />
          </label>
          {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
          {ok && <div className="text-sm text-teal">{t('punch.editSaved')}</div>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="btn-ghost text-sm py-2 px-4">{t('punch.editCancel')}</button>
            <button type="submit" disabled={busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
              {busy ? '…' : t('punch.editSave')}
            </button>
          </div>
        </form>

        <AuditSection recordId={record.id} />
      </div>
    </div>
  )
}

// ── Audit-Section (read-only) ──────────────────────────────────────────────
function AuditSection({ recordId }: { recordId: number }) {
  const t = useT()
  const lang = useLangCode()
  const [items, setItems] = useState<AuditEntry[] | null>(null)

  useEffect(() => {
    getRecordAudit(recordId)
      .then(setItems)
      .catch(() => setItems([]))
  }, [recordId])

  if (items === null) return null   // erst nach Load anzeigen — kein Flickern

  return (
    <details className="mt-5 group">
      <summary className="cursor-pointer text-xs font-bold tracking-wider uppercase text-app-text-mute select-none">
        🕓 {t('punch.auditTitle')} {items.length > 0 && <span className="text-app-text-dim">({items.length})</span>}
      </summary>
      {items.length === 0 ? (
        <div className="mt-2 text-xs text-app-text-mute italic">{t('punch.auditEmpty')}</div>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {items.map(a => <AuditRow key={a.id} a={a} t={t} lang={lang} />)}
        </ul>
      )}
    </details>
  )
}

function AuditRow({ a, t, lang }: { a: AuditEntry; t: any; lang: string }) {
  const fieldLabel = t(`punch.auditField.${a.field}`)
  const formatVal = (v: string | null): string => {
    if (v == null) return t('punch.auditUnset')
    if (a.field === 'clock_in' || a.field === 'clock_out') {
      try { return new Date(v).toLocaleString(lang, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) }
      catch { return v }
    }
    if (a.field === 'pause_seconds') {
      const s = parseInt(v); if (!isFinite(s)) return v
      return `${Math.round(s / 60)} min`
    }
    return v
  }
  const date = new Date(a.changedAt).toLocaleString(lang, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

  return (
    <li className="rounded-lg p-2.5 text-xs glass-flat">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{fieldLabel}</span>
        <span className="text-app-text-mute">
          {t('punch.auditFromTo', { from: formatVal(a.oldValue), to: formatVal(a.newValue) })}
        </span>
      </div>
      <div className="mt-0.5 text-app-text-mute">
        {t('punch.auditBy', { editor: a.editorName ?? '–' })} · {t('punch.auditWhen', { date })}
        {a.editorUserId !== a.targetUserId && (
          <span> · {a.targetName ?? a.targetUserId}</span>
        )}
      </div>
    </li>
  )
}

/// ISO → datetime-local-Input-Wert (lokale Zeitzone, Format `YYYY-MM-DDTHH:MM`)
function toLocalInputValue(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
function fromLocalInputValue(val: string): string {
  // Browser interpretiert datetime-local als lokale Zeit. new Date() konvertiert zu UTC ISO.
  return new Date(val).toISOString()
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmtCompact(seconds: number, t: ReturnType<typeof useT>): string {
  const { h, m } = fmtDuration(seconds)
  return t('punch.durationFmt', { h, m })
}
function formatRecordDate(iso: string, t: any, lang: 'de' | 'en'): string {
  const d = new Date(iso)
  const now = new Date()
  if (now.toDateString() === d.toDateString()) return t('punch.historyToday')
  const y = new Date(now); y.setDate(y.getDate() - 1)
  if (y.toDateString() === d.toDateString()) return t('punch.historyYesterday')
  return d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit' })
}
function Spinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="w-10 h-10 rounded-full border-4 border-app-border border-t-accent animate-spin" />
    </div>
  )
}
